/* eslint-disable complexity */
import { fastlyUrl, imageTypes } from "../../utils/helpers/cdn-image-url";
import { carDetailUrlAu } from "../../utils/helpers/get-detail-page-url";
import priceFormatter from "../../utils/helpers/price-formatter";
import { ORDER_TYPE } from "../../constants/app-constants";

export const CLEVERTAP_CURRENCY_CODE = "AUD";

export const CLEVERTAP_BOOKING_TYPE = {
    BUY_ONLINE: "buy online",
    TEST_DRIVE: "test drive"
};

export const getCleverTapBookingType = ({ orderType, paidTestDrive } = {}) => {
    const isTestDrive = paidTestDrive || [ORDER_TYPE.TEST_DRIVE, ORDER_TYPE.PRODUCT_TEST_DRIVE].includes(orderType);
    return isTestDrive ? CLEVERTAP_BOOKING_TYPE.TEST_DRIVE : CLEVERTAP_BOOKING_TYPE.BUY_ONLINE;
};

const getCarImageUrl = (carDetailsData = {}, config = {}) => {
    const { mainImage, exteriorImages, clp_image: clpImage } = carDetailsData || {};
    const path = (mainImage || {}).path || ((exteriorImages || []).find(image => image && image.path) || {}).path;
    const image = path || clpImage;
    if (!image) {
        return undefined;
    }
    if (/^https?:\/\//i.test(image)) {
        return image;
    }
    const imageHost = (config || {}).imageHost;
    return imageHost ? fastlyUrl(`${imageHost.replace(/\/+$/, "")}/`, image.replace(/^\/+/, ""), imageTypes.default) : undefined;
};

const getListingStatus = ({ booked, reserved, listingActive } = {}) => {
    if (booked) {
        return "booked";
    }
    if (reserved) {
        return "reserved";
    }
    return listingActive === false ? "inactive" : "active";
};

// Car attributes shared by CDP, wishlist and booking events
const carAttributes = (carDetailsData) => {
    const {
        fuelType,
        transmissionType,
        bodyType,
        body,
        odometerReading,
        kilometerDriven,
        city,
        cityName,
        state,
        stateCode,
        emiDetails
    } = carDetailsData || {};
    return {
        fuel_type: fuelType,
        transmission_type: transmissionType,
        body_type: bodyType || body,
        kilometerDriven: odometerReading || kilometerDriven,
        city: city || cityName,
        state: state || stateCode,
        emi: (emiDetails || {}).emiPerWeek,
        listing_status: getListingStatus(carDetailsData)
    };
};

const carData = (carDetailsData, {
    // list = "",
    // position = "",
    config,
    excludedKeys = [],
    prefix = "",
    shortListed,
    withCarAttributes = false
} = {}) => {
    const {
        appointmentId,
        carId,
        egc,
        make,
        bodyType,
        year,
        model,
        variant,
        orderId,
        body,
        price
    } = carDetailsData || {};
    const carName = `${make || ""} ${model || ""}`;

    const data = {
        [`${prefix}timestamp`]: Date.now(),
        [`${prefix}appid`]: appointmentId || carId,
        [`${prefix}car_name`]: `${year} ${make} ${model}`,
        [`${prefix}make`]: make,
        [`${prefix}model`]: model,
        [`${prefix}car_type`]: bodyType || body,
        [`${prefix}image`]: getCarImageUrl(carDetailsData, config),
        [`${prefix}year`]: year,
        [`${prefix}variant`]: variant,
        [`${prefix}cdp_link`]: carDetailUrlAu({year, carName, carId: appointmentId || carId}).absoluteURL,
        [`${prefix}price`]: egc,
        [`${prefix}discount`]: price - egc,
        ...(orderId && {[`${prefix}orderid`]: orderId}),
        ...(shortListed && {"wishlist_status": shortListed}),
        ...(withCarAttributes && carAttributes(carDetailsData))
        // "viewed_similar_cdp_link":
    };
    excludedKeys.forEach(item => delete data[item]);
    return data;
};

export default (payload, properties = {}) => {
    return  carData(payload, properties);
};

// Un-prefixed car fields used by Lead_created and payment_mode_selected
export const cleverTapCarSummary = (carDetailsData, { config } = {}) => {
    const { appointmentId, carId, make, model, year, variant, bodyType, body, egc } = carDetailsData || {};
    return {
        AppointmentId: appointmentId || carId,
        make,
        model,
        year,
        variant,
        carType: bodyType || body,
        price: egc,
        carDetailPageUrl: carDetailUrlAu({ year, carName: `${make || ""} ${model || ""}`, carId: appointmentId || carId }).absoluteURL,
        carImageURL: getCarImageUrl(carDetailsData, config)
    };
};

export const cleverTapBookingInitiated = (carDetailsData, { orderId, config, bookingType } = {}) => ({
    ...carData({ ...carDetailsData, orderId }, { prefix: "bi_", config, withCarAttributes: true }),
    bookingType,
    bookingStage: "initiated",
    checkoutPageURL: typeof window !== "undefined" ? window.location.href : undefined,
    currencyCode: CLEVERTAP_CURRENCY_CODE
});

export const cleverTapBookingConfirmed = (carDetailsData, { orderId, config, order = {} } = {}) => {
    const { bookingAmount, currency, paymentMode, deliveryMode } = order || {};
    return {
        ...carData({ ...carDetailsData, orderId }, { prefix: "bc_", config, withCarAttributes: true }),
        bookingType: getCleverTapBookingType(order || {}),
        bookingStatus: "confirmed",
        confirmedAt: Date.now(),
        depositAmount: bookingAmount,
        currencyCode: currency || CLEVERTAP_CURRENCY_CODE,
        paymentMode,
        deliveryMethod: deliveryMode
    };
};

export const CLEVERTAP_PAYMENT_STEP = {
    PAYMENT_OPTION: "payment_option",
    PAYMENT_METHOD: "payment_method"
};

export const cleverTapPaymentModeSelected = (carDetailsData, { order = {}, paymentMode, paymentStep, config } = {}) => {
    const { orderId, bookingAmount, currency } = order || {};
    return {
        ...cleverTapCarSummary(carDetailsData, { config }),
        orderId,
        paymentMode,
        paymentStep,
        selectedAt: Date.now(),
        bookingType: getCleverTapBookingType(order || {}),
        bookingStage: "payment",
        depositAmount: bookingAmount,
        currencyCode: currency || CLEVERTAP_CURRENCY_CODE
    };
};

export const cleverTapTradeinDetails = (payload) => {
    const {vehicleMetaData: {make, model, year, variant} = {}, vin, odometerReading, regNo } = payload;
    return ({
        "trade_in_make": make,
        "trade_in_model": model,
        "trade_in_year": year,
        "trade_in_km": odometerReading,
        "trade_in_timestamp": Date.now(),
        "trade_in_variant": variant,
        ...(vin && {"trade_in_vin": vin}),
        ...(regNo && {"trade_in_registration_number": regNo})
    });
};

export const c2bUserDetails = (tradeInData) => {
    const {userEmail, buildYear, fullName, vehicleMetaData, userEnteredYear, makeModelFlow, state, dsOfferedPrice, c2bEligible, c2bOrderId, offeredPrice, showPathName, pathname, showValuationDate, firstTradeMode, sellingType, mobile, desktop} = tradeInData;
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
        .replace(/\//g, "-");

    return ({
        "User ID": userEmail,
        ...(fullName && {"Full Name": fullName}),
        "C2B Make": vehicleMetaData?.make,
        "C2B Model": vehicleMetaData?.model,
        "C2B Build Year": (makeModelFlow ? vehicleMetaData?.year : userEnteredYear) || buildYear,
        "C2B Car State": state,
        "C2B DS Price": dsOfferedPrice,
        "C2B Eligibilty": c2bEligible,
        "C2B Platform": desktop ? "web" : "msite",
        "C2B Order ID": c2bOrderId,
        "C2B Offered Price": offeredPrice,
        ...(showValuationDate && {"C2B valuation Shown Date": formattedDate}),
        ...(showPathName && {"C2B personalised valuation page link": pathname}),
        "C2B First Trade Mode": firstTradeMode,
        ...(sellingType && {"C2B Selling Type": sellingType}),
        ...(mobile && {"Phone Number": `+614${mobile}`})
    });

};

export const c2bUserDetailsForCarValuationCalculator = (tradeInData) => {
    const {userEmail, email, buildYear, vehicleMetaData, userEnteredYear, makeModelFlow, c2bEligible, lowerLimitOfferedPrice, upperLimitOfferedPrice, mobile } = tradeInData;
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
        .replace(/\//g, "-");

    return ({
        "User ID": userEmail || email,
        "C2B_PriceSignup_Make": vehicleMetaData?.make,
        "C2B_PriceSignup_Model": vehicleMetaData?.model,
        "C2B_PriceSignup_BuildYear": (makeModelFlow ? vehicleMetaData?.year : userEnteredYear) || buildYear,
        "C2B_PriceSignup_Eligibility": c2bEligible,
        "C2B_PriceSignup_RangePrice": `$${priceFormatter(lowerLimitOfferedPrice)} to $${priceFormatter(upperLimitOfferedPrice)}`,
        "C2B_PriceSignup_DateOfSignup": formattedDate,
        "C2B_PriceSignup_MobileNumber": `+614${mobile}`
    });
};
